// Table — wrappers tipados sobre <table> nativo con estilos del sistema.
//
//   <Table>
//     <THead>
//       <TR>
//         <TH>Período</TH>
//         <TH align="right">Real</TH>
//       </TR>
//     </THead>
//     <TBody>
//       <TR>
//         <TD>2026-05-25</TD>
//         <TD align="right">500</TD>
//       </TR>
//     </TBody>
//   </Table>
//
// Alineación a la derecha para columnas numéricas usa tabular-nums.

import {
  forwardRef,
  type CSSProperties,
  type HTMLAttributes,
  type TdHTMLAttributes,
  type ThHTMLAttributes,
} from 'react';
import { cn } from '../../lib/utils';

interface TableProps extends HTMLAttributes<HTMLTableElement> {
  /** Wrapper externo aplica overflow-x-auto + border + rounded. */
  containerClassName?: string;
  /**
   * Si se setea, el wrapper habilita scroll vertical interno con este alto
   * máximo (ej. '70vh', '600px'). Combinar con `<THead sticky>` para que el
   * encabezado quede fijo mientras se hace scroll de las filas.
   */
  maxHeight?: string;
}

export const Table = forwardRef<HTMLTableElement, TableProps>(function Table(
  { className, containerClassName, maxHeight, style, ...props },
  ref,
) {
  const wrapperStyle: CSSProperties | undefined = maxHeight ? { maxHeight, ...style } : style;
  return (
    <div
      className={cn(
        'overflow-x-auto rounded border border-border',
        maxHeight && 'overflow-y-auto',
        containerClassName,
      )}
      style={wrapperStyle}
    >
      <table ref={ref} className={cn('min-w-full text-sm', className)} {...props} />
    </div>
  );
});

interface THeadProps extends HTMLAttributes<HTMLTableSectionElement> {
  /** Fija el encabezado al hacer scroll vertical (usar con `<Table maxHeight>`). */
  sticky?: boolean;
}

export const THead = forwardRef<HTMLTableSectionElement, THeadProps>(function THead(
  { className, sticky, ...props },
  ref,
) {
  return (
    <thead
      ref={ref}
      className={cn('bg-surface-overlay', sticky && 'sticky top-0 z-10', className)}
      {...props}
    />
  );
});

export const TBody = forwardRef<HTMLTableSectionElement, HTMLAttributes<HTMLTableSectionElement>>(
  function TBody({ className, ...props }, ref) {
    return <tbody ref={ref} className={className} {...props} />;
  },
);

export const TFoot = forwardRef<HTMLTableSectionElement, HTMLAttributes<HTMLTableSectionElement>>(
  function TFoot({ className, ...props }, ref) {
    return (
      <tfoot
        ref={ref}
        className={cn(
          'border-t border-border bg-surface-overlay text-xs font-medium text-fg-secondary',
          className,
        )}
        {...props}
      />
    );
  },
);

export const TR = forwardRef<HTMLTableRowElement, HTMLAttributes<HTMLTableRowElement>>(function TR(
  { className, ...props },
  ref,
) {
  return (
    <tr
      ref={ref}
      className={cn(
        'border-b border-border last:border-b-0 hover:bg-surface-overlay/40',
        className,
      )}
      {...props}
    />
  );
});

interface ThProps extends ThHTMLAttributes<HTMLTableCellElement> {
  align?: 'left' | 'center' | 'right';
}
export const TH = forwardRef<HTMLTableCellElement, ThProps>(function TH(
  { className, align = 'left', ...props },
  ref,
) {
  return (
    <th
      ref={ref}
      className={cn(
        'border-b border-border px-3 py-2 text-[11px] font-medium uppercase tracking-wider text-fg-tertiary',
        align === 'right' && 'text-right',
        align === 'center' && 'text-center',
        align === 'left' && 'text-left',
        className,
      )}
      {...props}
    />
  );
});

interface TdProps extends TdHTMLAttributes<HTMLTableCellElement> {
  align?: 'left' | 'center' | 'right';
  /** Si true aplica font-variant tabular-nums + monospace para números. */
  numeric?: boolean;
}
export const TD = forwardRef<HTMLTableCellElement, TdProps>(function TD(
  { className, align = 'left', numeric, style, ...props },
  ref,
) {
  return (
    <td
      ref={ref}
      className={cn(
        'px-3 py-2 text-sm text-fg',
        align === 'right' && 'text-right',
        align === 'center' && 'text-center',
        className,
      )}
      style={numeric ? { fontVariantNumeric: 'tabular-nums', ...style } : style}
      {...props}
    />
  );
});
